import React, { useState, useRef, useEffect } from 'react';
import { FaStop, FaCircle } from 'react-icons/fa';
import { AudioButton, RecordingIndicatorWrapper, TooltipWrapper, TooltipText } from './styles';

const AudioRecorder = ({ onAudioRecorded, isRecording }) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const MAX_RECORDING_TIME = 120; // 2 minutes

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    if (isRecording) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          if (isSafari) {
            handleSafariRecording(stream);
          } else {
            handleStandardRecording(stream);
          }
        })
        .catch((error) => {
          console.error('Error accessing microphone:', error);
        });
    } else {
      stopRecording();
      clearInterval(timerRef.current);
      setRecordingTime(0);
    }
    // Clean up when component unmounts or when recording stops
    return () => {
      stopRecording();
    };
  }, [isRecording]);

  const handleSafariRecording = (stream) => {
    console.log('Handling Safari recording');
    let mediaRecorder;
    let options = null;

    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      options = { mimeType: 'audio/mp4' };
    } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
      options = { mimeType: 'audio/mpeg' };
    } else if (MediaRecorder.isTypeSupported('audio/webm')) {
      options = { mimeType: 'audio/webm' };
    } else {
      options = {};
    }

    try {
      mediaRecorder = new MediaRecorder(stream, options);
    } catch (e) {
      console.error('Error creating MediaRecorder with options:', e);
      try {
        mediaRecorder = new MediaRecorder(stream);
      } catch (err) {
        console.error('Error creating MediaRecorder without options:', err);
        return;
      }
    }

    mediaRecorderRef.current = mediaRecorder;
    const recordedChunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const recordedBlob = new Blob(recordedChunks, { type: mediaRecorder.mimeType });
      console.log('Recorded Blob:', recordedBlob);
      onAudioRecorded(recordedBlob);
      clearInterval(timerRef.current);
    };

    mediaRecorder.start(1000); // Start with a timeslice of 1 second
    startTimer();
  };

  const handleStandardRecording = (stream) => {
    console.log('Handling standard recording');
    let mediaRecorder;
    let options = null;

    if (MediaRecorder.isTypeSupported('audio/webm')) {
      options = { mimeType: 'audio/webm' };
    } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
      options = { mimeType: 'audio/mp4' };
    } else if (MediaRecorder.isTypeSupported('audio/mpeg')) {
      options = { mimeType: 'audio/mpeg' };
    } else {
      options = {};
    }

    try {
      mediaRecorder = new MediaRecorder(stream, options);
    } catch (e) {
      console.error('Error creating MediaRecorder with options:', e);
      try {
        mediaRecorder = new MediaRecorder(stream);
      } catch (err) {
        console.error('Error creating MediaRecorder without options:', err);
        return;
      }
    }

    mediaRecorderRef.current = mediaRecorder;
    const recordedChunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const recordedBlob = new Blob(recordedChunks, { type: mediaRecorder.mimeType });
      console.log('Recorded Blob:', recordedBlob);
      onAudioRecorded(recordedBlob);
      clearInterval(timerRef.current);
    };

    mediaRecorder.start(1000); // Start with a timeslice of 1 second
    startTimer();
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime((prevTime) => {
        const newTime = prevTime + 1;
        if (newTime >= MAX_RECORDING_TIME) {
          stopRecording();
        }
        return newTime;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    }
    clearInterval(timerRef.current);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="audio-recorder">
      {isRecording ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TooltipWrapper>
              <AudioButton type="button" onClick={stopRecording}>
                <FaStop className="stop-icon" />
              </AudioButton>
              <TooltipText>Finalizar Gravação</TooltipText>
            </TooltipWrapper>
            <RecordingIndicatorWrapper>
              <RecordingIndicator />
              <div style={{ marginLeft: '15px' }}>Gravando...</div>
            </RecordingIndicatorWrapper>
          </div>
          <div style={{ color: 'white', marginRight: '10px', display: 'flex', alignItems: 'center' }}>
            {formatTime(recordingTime)}
            {recordingTime >= 95 && (
              <span style={{ marginLeft: '10px', color: 'red' }}>
                Seu áudio irá encerrar em {MAX_RECORDING_TIME - recordingTime} seg
              </span>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RecordingIndicator = () => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <FaCircle style={{ animation: 'blink 1.4s infinite both' }} />
      <FaCircle style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.2s' }} />
      <FaCircle style={{ animation: 'blink 1.4s infinite both', animationDelay: '0.4s' }} />
      <style>
        {`
          @keyframes blink {
            0% { opacity: 0.2; }
            20% { opacity: 1; }
            100% { opacity: 0.2; }
          }
        `}
      </style>
    </div>
  );
};

export default AudioRecorder;
